import { useMsal } from '@azure/msal-react'
import { EFinancingCompany } from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material'
import { useMockLogin } from '@src/data/api/user/user-api'
import { useOptionalUser } from '@src/providers/UserProvider'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { loginRequest } from '../../authConfig'
import { useDevTools } from '../../data/types'
import ListMockedLoginDialog from './components/ListMockedLoginDialog'

const LoginPage = () => {
  const user = useOptionalUser()
  const [mockLogin] = useMockLogin()
  const [isLoginMockDialogOpen, setIsLoginMockDialogOpen] = useState<boolean>(false)
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { prefersDarkMode, setDarkMode } = usePreferences()
  const { instance } = useMsal()
  document.title = t('login.login')

  if (user) {
    return <Navigate to="/home" replace />
  }

  const onMockedLoginSelected = (username: string, companyIds: EFinancingCompany[]) => {
    mockLogin({ username, password: 'ok', companyIds }).catch(reportErrorToConsole)
  }

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(reportErrorToConsole)
  }

  const showMockLoginOnLoginPage =
    useDevTools && getEnvRuntimeVariable<boolean>(import.meta.env.VITE_SHOW_MOCK_LOGIN_ON_LOGIN_PAGE)

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en'
  const isDark = theme.palette.mode === 'dark'

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        inset: 0,
        background: theme.palette.background.default,
      }}
    >
      {showMockLoginOnLoginPage && (
        <ListMockedLoginDialog
          onMockedLoginSelected={onMockedLoginSelected}
          open={isLoginMockDialogOpen}
          onClose={() => setIsLoginMockDialogOpen(false)}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: 440,
          px: 6,
          py: 6,
          borderRadius: '20px',
          background: theme.palette.background.paper,
          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => setDarkMode(!prefersDarkMode)}
          sx={{ position: 'absolute', top: 12, left: 12, p: 0.5 }}
        >
          {isDark ? (
            <Brightness7Icon sx={{ fontSize: 18, color: '#9aa3b2' }} />
          ) : (
            <Brightness4Icon sx={{ fontSize: 18, color: '#9aa3b2' }} />
          )}
        </IconButton>

        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #d0d0d0',
          }}
        >
          {(['fr', 'en'] as const).map((lang) => (
            <Button
              key={lang}
              size="small"
              onClick={() => i18n.changeLanguage(lang)}
              sx={{
                minWidth: 28,
                px: 1,
                py: 0.15,
                fontSize: '0.6rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                textTransform: 'uppercase',
                borderRadius: 0,
                color: currentLang === lang ? '#FFFFFF' : '#8a8a8a',
                backgroundColor: currentLang === lang ? '#0078D4' : 'transparent',
                '&:hover': {
                  backgroundColor: currentLang === lang ? '#005FA3' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              {lang}
            </Button>
          ))}
        </Box>

        <Box
          component="img"
          src={isDark ? '/images/ifinance-white-logo.svg' : '/images/ifinance-logo.svg'}
          alt="iFinance"
          sx={{ width: 200, mt: 2, mb: 1 }}
        />

        <Box
          sx={{
            width: '60%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #08abf0, transparent)',
            my: 2,
          }}
        />

        <Button
          type="button"
          variant="contained"
          fullWidth
          onClick={handleLogin}
          sx={{
            background: '#0078D4',
            color: '#FFFFFF',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '15px',
            letterSpacing: '0.05em',
            textTransform: 'none',
            borderRadius: '12px',
            py: 1.4,
            boxShadow: 'none',
            '&:hover': {
              background: '#005FA3',
              boxShadow: 'none',
            },
          }}
        >
          {t('login.login')}
        </Button>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 2,
            mb: 1,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, color: '#9aa3b2' }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '11px',
              color: '#9aa3b2',
            }}
          >
            {t('login.securityNote')}
          </Typography>
        </Box>

        {showMockLoginOnLoginPage && (
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={() => setIsLoginMockDialogOpen(true)}
            sx={{ mt: 1 }}
          >
            Cas de test de login
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default LoginPage
